/*
 * Circulei · filtro de disponibilidade por data (opção D, híbrida)
 *
 * - A Shopify continua dona da coleção: filtros, ordenação e quais peças pertencem a ela.
 *   Lemos a lista ordenada pelo template alternativo  ?view=circulei-handles  (250 por página).
 * - A API (Vercel) responde só quais handles estão livres no período (com o intervalo entre aluguéis).
 * - Cruzamos as duas listas, paginamos de 48 em 48 e pedimos à Shopify o cartão pronto de cada peça
 *   (section circulei-availability-card), então o visual é idêntico ao da grade original.
 * - O navegador nunca fala com o Cowlendar. Nenhum token existe neste arquivo.
 * - Em caso de erro, a grade original da Shopify continua visível (nunca escondemos tudo em silêncio).
 */
(() => {
  if (window.customElements.get("circulei-availability")) return;

  const PARAM_START = "inicio";
  const PARAM_END = "devolucao";
  const MAX_DAYS = 30;
  const DISCLAIMER_KEY = "circulei-availability-disclaimer";
  const DISCLAIMER_MS = 8000;
  const PARAM_PAGE = "page";
  const HANDLES_VIEW = "circulei-handles";
  const CARD_SECTION = "circulei-availability-card";
  const PER_PAGE = 48;
  const NATIVE_PER_PAGE = 48; // "produtos por página" da seção da coleção; se mudar, o modo data continua certo (só fica mais lento)
  const MAX_COLLECTION_PAGES = 20; // 20 × 250 = 5.000 peças
  const CARD_CONCURRENCY = 8;
  const API_TIMEOUT_MS = 10000;
  const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
  const debug = new URLSearchParams(location.search).has("circulei_debug");

  const ICON_LEFT = '<svg aria-hidden="true" focusable="false" fill="none" width="11" viewBox="0 0 10 10"><path d="M7 1 3 5l4 4" stroke="currentColor" stroke-linecap="square"/></svg>';
  const ICON_RIGHT = '<svg aria-hidden="true" focusable="false" fill="none" width="11" viewBox="0 0 10 10"><path d="m3 9 4-4-4-4" stroke="currentColor" stroke-linecap="square"/></svg>';

  // ---------------------------------------------------------------------------------------------
  // Datas (sempre como YYYY-MM-DD, sem fuso: o calendário escolhido pela cliente)
  // ---------------------------------------------------------------------------------------------
  const pad = (n) => String(n).padStart(2, "0");
  const toDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const addDays = (day, n) => {
    const [y, m, d] = day.split("-").map(Number);
    return toDay(new Date(y, m - 1, d + n));
  };
  const isRealDay = (day) => {
    if (!DAY_RE.test(day || "")) return false;
    const [y, m, d] = day.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  };
  const formatBr = (day) => day.split("-").reverse().join("/");
  const daysBetween = (start, end) => {
    const [y1, m1, d1] = start.split("-").map(Number);
    const [y2, m2, d2] = end.split("-").map(Number);
    return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 864e5) + 1; // inclusivo
  };

  class CirculeiAvailability extends HTMLElement {
    connectedCallback() {
      this.startInput = this.querySelector('[data-role="start"]');
      this.endInput = this.querySelector('[data-role="end"]');
      this.globalMax = this.startInput.max;
      this.clearButton = this.querySelector('[data-role="clear"]');
      this.statusEl = this.querySelector('[data-role="status"]');
      this.disclaimerEl = this.querySelector('[data-role="disclaimer"]');
      this.apiUrl = this.dataset.apiUrl;
      this.collectionUrl = this.dataset.collectionUrl;
      // Duração mínima do aluguel (setting da section, padrão 4). Também é a duração sugerida.
      this.minDays = Math.min(Math.max(Number(this.dataset.minDays) || 4, 1), MAX_DAYS);

      this.availabilityCache = new Map();
      this.handlesCache = new Map();
      this.cardCache = new Map();
      this.requestId = 0;
      this.controller = null;
      this.active = false; // a grade está em "modo data"
      this.suspended = false; // após um erro, não reaplicamos sozinhos até a cliente mexer no filtro

      this.mainSection = document.querySelector(".shopify-section--main-collection");
      if (!this.mainSection || !this.apiUrl || !this.collectionUrl) return; // sem grade: não faz nada

      let debounce;
      const scheduleApply = () => {
        this.suspended = false;
        clearTimeout(debounce);
        debounce = setTimeout(() => this.apply({ resetPage: true }), 350);
      };
      for (const input of [this.startInput, this.endInput]) {
        input.addEventListener("input", () => this.syncEmpty());
      }
      // Ao escolher o início, a devolução é sugerida com a duração padrão (ex.: 4 dias), e pode ser alterada.
      this.startInput.addEventListener("change", () => {
        this.suggestEnd();
        this.syncEmpty();
        scheduleApply();
      });
      this.endInput.addEventListener("change", () => {
        this.syncEmpty();
        scheduleApply();
      });
      this.clearButton.addEventListener("click", () => this.clear());

      // Paginação do modo data (links gerados por nós)
      this.mainSection.addEventListener("click", (event) => {
        const link = event.target.closest("[data-circulei-page]");
        if (!link || !this.active) return;
        event.preventDefault();
        this.apply({ page: Number(link.dataset.circuleiPage), scroll: true });
      });

      // O tema substitui o conteúdo da section principal a cada filtro/ordenação (evento facet:update).
      // Quando isso acontece com uma data ativa, refazemos o modo data com os novos filtros.
      this.observer = new MutationObserver(() => {
        if (this.selectedStart() && !this.suspended) this.apply({ resetPage: true });
      });
      this.observer.observe(this.mainSection, { childList: true });

      // Estado inicial vindo da URL (link compartilhado, voltar do produto, recarregar)
      const params = new URLSearchParams(location.search);
      const start = params.get(PARAM_START);
      const end = params.get(PARAM_END);
      if (start && isRealDay(start)) {
        this.startInput.value = start;
        if (end && isRealDay(end)) this.endInput.value = end;
        this.suggestEnd();
        this.syncEmpty();
        this.apply({ page: Math.max(1, Number(params.get(PARAM_PAGE)) || 1) });
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    selectedStart() {
      return this.startInput.value || "";
    }

    selectedEnd() {
      return this.endInput.value || "";
    }

    /** Duração inclusiva (início e devolução contam). */
    selectedDays() {
      const start = this.selectedStart();
      const end = this.selectedEnd();
      return isRealDay(start) && isRealDay(end) ? daysBetween(start, end) : null;
    }

    syncEmpty() {
      for (const input of [this.startInput, this.endInput]) {
        input.closest(".circulei-availability__date").dataset.empty = String(!input.value);
      }
    }

    /**
     * Limita a devolução ao período permitido (mínimo de minDays, máximo de MAX_DAYS) e sugere
     * início + mínimo quando ela está vazia ou fora dele.
     */
    suggestEnd() {
      const start = this.selectedStart();
      if (!isRealDay(start)) return;
      const minEnd = addDays(start, this.minDays - 1);
      const maxEnd = addDays(start, MAX_DAYS - 1);
      this.endInput.min = minEnd;
      this.endInput.max = maxEnd;
      const end = this.selectedEnd();
      if (!isRealDay(end) || end < minEnd || end > maxEnd) this.endInput.value = minEnd;
    }

    validate(start, end) {
      if (!isRealDay(start)) return "Escolha uma data de início válida.";
      const today = toDay(new Date());
      if (start < today) return "Escolha uma data de início a partir de hoje.";
      if (this.globalMax && start > this.globalMax) return "Essa data ainda não está aberta para reservas.";
      if (!isRealDay(end)) return "Escolha a data de devolução.";
      if (end < start) return "A devolução precisa ser depois do início.";
      if (daysBetween(start, end) < this.minDays) return `O aluguel tem no mínimo ${this.minDays} dias.`;
      if (daysBetween(start, end) > MAX_DAYS) return `O aluguel pode ter no máximo ${MAX_DAYS} dias.`;
      return null;
    }

    // O aviso é sempre em minúsculas (pedido da Júlia).
    setStatus(text, state = "") {
      this.statusEl.textContent = text.toLocaleLowerCase("pt-BR");
      this.statusEl.dataset.state = state;
    }

    periodText(start, days) {
      const end = addDays(start, days - 1);
      return days === 1 ? `Aluguel de 1 dia: ${formatBr(start)}` : `Aluguel de ${days} dias: ${formatBr(start)} a ${formatBr(end)}`;
    }

    updateUrl({ start, days, page }) {
      const url = new URL(location.href);
      for (const key of [PARAM_START, PARAM_END, PARAM_PAGE]) url.searchParams.delete(key);
      if (start) {
        url.searchParams.set(PARAM_START, start);
        url.searchParams.set(PARAM_END, addDays(start, days - 1));
        if (page > 1) url.searchParams.set(PARAM_PAGE, String(page));
      }
      history.replaceState(history.state, "", url.toString());
    }

    // -------------------------------------------------------------------------------------------
    // Fluxo principal
    // -------------------------------------------------------------------------------------------
    async apply({ page = 1, resetPage = false, scroll = false } = {}) {
      const start = this.selectedStart();
      if (!start) return this.clear();
      if (!this.selectedEnd()) this.suggestEnd();
      const end = this.selectedEnd();
      const days = this.selectedDays();

      const invalid = this.validate(start, end);
      if (invalid) {
        this.setStatus(invalid, "error");
        return;
      }

      const requestId = ++this.requestId;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      if (resetPage) page = 1;
      this.clearButton.hidden = false;
      this.updateUrl({ start, days, page });
      this.setStatus("buscando peças disponíveis…", "loading");
      document.documentElement.classList.add("circulei-date-loading");

      try {
        const [availability, handles] = await Promise.all([
          this.fetchAvailability(start, days, signal),
          this.fetchCollectionHandles(signal),
        ]);
        if (requestId !== this.requestId) return;

        const free = new Set(availability.available_handles);
        const ordered = handles.filter((handle) => free.has(handle));
        const total = ordered.length;
        const pages = Math.max(1, Math.ceil(total / PER_PAGE));
        page = Math.min(Math.max(1, page), pages);
        if (debug) {
          const missing = handles.filter((h) => !free.has(h));
          console.info("[circulei] coleção:", handles.length, "| livres na coleção:", total, "| fora da lista de livres:", missing.length, missing.slice(0, 50));
        }

        const slice = ordered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
        await this.prefetchNativeCards(slice, handles, signal);
        const cards = await this.fetchCards(slice, signal);
        if (requestId !== this.requestId) return;

        this.updateUrl({ start, days, page });
        this.flashDisclaimer();
        this.render({ cards, total, page, pages, start, days, stale: availability.stale === true });
        if (scroll) this.mainSection.querySelector(".collection")?.scrollIntoView({ block: "start", behavior: "smooth" });
      } catch (error) {
        if (error?.name === "AbortError" && requestId !== this.requestId) return;
        if (requestId !== this.requestId) return;
        if (debug) console.warn("[circulei] erro:", error);
        this.showError();
      } finally {
        if (requestId === this.requestId) document.documentElement.classList.remove("circulei-date-loading");
      }
    }

    async fetchAvailability(start, days, signal) {
      const key = `${start}|${days}`;
      const cached = this.availabilityCache.get(key);
      if (cached && Date.now() - cached.at < 60000) return cached.data;

      const url = new URL(this.apiUrl);
      url.searchParams.set("start", start);
      url.searchParams.set("days", String(days));
      const timeout = setTimeout(() => this.controller?.abort(), API_TIMEOUT_MS);
      try {
        const response = await fetch(url, { signal, mode: "cors", credentials: "omit" });
        if (!response.ok) throw new Error(`availability_http_${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data.available_handles)) throw new Error("availability_bad_response");
        this.availabilityCache.set(key, { at: Date.now(), data });
        return data;
      } finally {
        clearTimeout(timeout);
      }
    }

    /** Parâmetros atuais da coleção (filtros e ordenação), sem os nossos nem os de paginação/renderização. */
    collectionParams() {
      const params = new URLSearchParams(location.search);
      for (const key of [PARAM_START, PARAM_END, PARAM_PAGE, "section_id", "view", "circulei_debug"]) params.delete(key);
      params.sort();
      return params;
    }

    /**
     * Pega os cartões das páginas normais da coleção (48 por página, já renderizadas e em cache na Shopify):
     * normalmente 1 ou 2 pedidos em vez de 1 por peça. O que não for encontrado aqui é buscado peça a peça.
     */
    async prefetchNativeCards(slice, collectionHandles, signal) {
      const missing = slice.filter((handle) => !this.cardCache.has(handle));
      if (missing.length === 0) return;
      const position = new Map(collectionHandles.map((handle, index) => [handle, index]));
      const pages = new Set(missing.map((handle) => Math.floor(position.get(handle) / NATIVE_PER_PAGE) + 1));
      const sectionId = this.mainSection.id.replace("shopify-section-", "");

      await Promise.all(
        [...pages].map(async (page) => {
          const params = this.collectionParams();
          params.set("page", String(page));
          params.set("section_id", sectionId);
          try {
            const response = await fetch(`${this.collectionUrl}?${params.toString()}`, { signal, credentials: "same-origin" });
            if (!response.ok) return;
            const doc = new DOMParser().parseFromString(await response.text(), "text/html");
            for (const card of doc.querySelectorAll("product-card[handle]")) {
              card.removeAttribute("reveal-on-scroll"); // a animação de entrada do tema não roda em cartões inseridos depois
              this.cardCache.set(card.getAttribute("handle"), card.outerHTML);
            }
          } catch (error) {
            if (error?.name === "AbortError") throw error;
          }
        }),
      );
    }

    /** Handles da coleção na ordem da Shopify, com os filtros e a ordenação atuais da URL. */
    async fetchCollectionHandles(signal) {
      const params = this.collectionParams();
      const key = params.toString();
      if (this.handlesCache.has(key)) return this.handlesCache.get(key);

      const pageUrl = (page) => {
        const p = new URLSearchParams(params);
        p.set("view", HANDLES_VIEW);
        p.set("page", String(page));
        return `${this.collectionUrl}?${p.toString()}`;
      };
      const getPage = async (page) => {
        const response = await fetch(pageUrl(page), { signal, credentials: "same-origin" });
        if (!response.ok) throw new Error(`collection_http_${response.status}`);
        const data = JSON.parse(await response.text());
        if (!Array.isArray(data.handles)) throw new Error("collection_bad_response");
        return data;
      };

      const first = await getPage(1);
      const totalPages = Math.min(Number(first.pages) || 1, MAX_COLLECTION_PAGES);
      const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => getPage(i + 2)));
      const handles = [...new Set([first, ...rest].flatMap((page) => page.handles))];
      this.handlesCache.set(key, handles);
      return handles;
    }

    /** Cartões prontos da Shopify, na mesma ordem dos handles. Falhas individuais são puladas. */
    async fetchCards(handles, signal) {
      const root = window.Shopify?.routes?.root || "/";
      const results = new Array(handles.length).fill(null);
      let cursor = 0;
      const worker = async () => {
        while (cursor < handles.length) {
          const index = cursor++;
          const handle = handles[index];
          if (this.cardCache.has(handle)) {
            results[index] = this.cardCache.get(handle);
            continue;
          }
          try {
            const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION}`, { signal, credentials: "same-origin" });
            if (!response.ok) continue;
            const doc = new DOMParser().parseFromString(await response.text(), "text/html");
            const card = doc.querySelector("product-card");
            if (!card) continue;
            this.cardCache.set(handle, card.outerHTML);
            results[index] = card.outerHTML;
          } catch (error) {
            if (error?.name === "AbortError") throw error;
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(CARD_CONCURRENCY, handles.length) }, worker));
      return results.filter(Boolean);
    }

    // -------------------------------------------------------------------------------------------
    // Renderização
    // -------------------------------------------------------------------------------------------
    render({ cards, total, page, pages, start, days, stale }) {
      const main = this.mainSection.querySelector(".collection__main");
      const productList = this.mainSection.querySelector("product-list");
      this.active = true;

      // Sem quantidade de peças (pedido da Júlia): só o período escolhido.
      this.setStatus(`${this.periodText(start, days)}${stale ? " · disponibilidade em atualização" : ""}`, total === 0 ? "empty" : "ready");

      // O contador nativo da Shopify ("X produtos") não vale no modo data: escondemos enquanto ele estiver ativo.
      const counter = this.mainSection.querySelector(".collection-toolbar__products-count");
      if (counter) counter.hidden = true;

      if (!main || !productList) {
        // A própria Shopify não achou peças com esses filtros: o estado vazio dela já está na tela.
        return;
      }

      const template = document.createElement("template");
      template.innerHTML = cards.join("");
      productList.replaceChildren(template.content);
      productList.hidden = total === 0;

      main.querySelector(".circulei-availability-empty")?.remove();
      if (total === 0) {
        const empty = document.createElement("div");
        empty.className = "empty-state circulei-availability-empty";
        empty.innerHTML =
          '<div class="v-stack gap-4"><p>Nenhuma peça disponível para este período.</p>' +
          '<p class="text-sm text-subdued">Tente outra data de início ou menos dias.</p></div>';
        productList.after(empty);
      }

      main.querySelectorAll("nav.pagination").forEach((nav) => nav.remove());
      if (pages > 1) productList.after(this.buildPagination(page, pages, start, days));
    }

    buildPagination(page, pages, start, days) {
      const nav = document.createElement("nav");
      nav.className = "pagination";
      nav.setAttribute("role", "navigation");
      nav.setAttribute("aria-label", "Paginação");

      const href = (n) => {
        const url = new URL(location.href);
        url.searchParams.set(PARAM_START, start);
        url.searchParams.set(PARAM_END, addDays(start, days - 1));
        if (n > 1) url.searchParams.set(PARAM_PAGE, String(n));
        else url.searchParams.delete(PARAM_PAGE);
        return url.pathname + url.search;
      };
      const link = (n, content, label, rel) => {
        const a = document.createElement("a");
        a.className = "pagination__link h6";
        a.href = href(n);
        a.dataset.circuleiPage = String(n);
        a.setAttribute("aria-label", label);
        if (rel) a.rel = rel;
        a.innerHTML = content;
        return a;
      };
      const span = (content, current) => {
        const s = document.createElement("span");
        s.className = "pagination__link pagination__link--disabled h6";
        if (current) s.setAttribute("aria-current", "page");
        s.textContent = content;
        return s;
      };

      if (page > 1) nav.append(link(page - 1, ICON_LEFT, `Ir para a página ${page - 1}`, "prev"));
      const shown = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
      let previous = 0;
      for (const n of [...shown].sort((a, b) => a - b)) {
        if (n - previous > 1) nav.append(span("…", false));
        nav.append(n === page ? span(String(n), true) : link(n, String(n), `Ir para a página ${n}`));
        previous = n;
      }
      if (page < pages) nav.append(link(page + 1, ICON_RIGHT, `Ir para a página ${page + 1}`, "next"));
      return nav;
    }

    /** Aviso de simulação: aparece por alguns segundos, só na primeira busca de cada visita. */
    flashDisclaimer() {
      const el = this.disclaimerEl;
      if (!el || this.disclaimerShown) return;
      this.disclaimerShown = true;
      try {
        if (sessionStorage.getItem(DISCLAIMER_KEY) === "1") return;
        sessionStorage.setItem(DISCLAIMER_KEY, "1");
      } catch {
        // sem sessionStorage (aba anônima etc.): mostra uma vez por carregamento de página
      }
      el.classList.remove("is-fading");
      el.hidden = false;
      clearTimeout(this.disclaimerTimer);
      this.disclaimerTimer = setTimeout(() => {
        el.classList.add("is-fading");
        this.disclaimerTimer = setTimeout(() => {
          el.hidden = true;
          el.classList.remove("is-fading");
        }, 700);
      }, DISCLAIMER_MS);
    }

    showError() {
      this.suspended = true;
      this.setStatus("Não conseguimos verificar a disponibilidade agora. Mostrando todas as peças.", "error");
      if (this.active) this.reloadNative();
    }

    clear() {
      this.requestId++;
      this.suspended = false;
      this.controller?.abort();
      document.documentElement.classList.remove("circulei-date-loading");
      this.startInput.value = "";
      this.endInput.value = "";
      this.endInput.removeAttribute("min");
      this.endInput.max = this.globalMax;
      this.syncEmpty();
      this.clearButton.hidden = true;
      clearTimeout(this.disclaimerTimer);
      if (this.disclaimerEl) this.disclaimerEl.hidden = true;
      this.setStatus("");
      this.updateUrl({ start: null });
      if (this.active) this.reloadNative();
    }

    /** Volta para a grade original pedindo a section à Shopify pelo mesmo caminho do tema (facet:update). */
    reloadNative() {
      this.active = false;
      const sectionId = this.mainSection.id.replace("shopify-section-", "");
      const url = new URL(location.href);
      for (const key of [PARAM_START, PARAM_END, PARAM_PAGE]) url.searchParams.delete(key);
      url.searchParams.set("section_id", sectionId);
      document.dispatchEvent(new CustomEvent("facet:update", { bubbles: true, detail: { url } }));
    }
  }

  window.customElements.define("circulei-availability", CirculeiAvailability);
})();
